import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { DropdownCaret } from "components/BudgetBuilder/Overview/OverviewLineItemsList/DropdownCaret";
import { useIsExpandedContext } from "components/BudgetBuilder/Overview/OverviewLineItemsList/OverviewLineItemsList";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Table from "components/Table";
import { budgetBuilderSelectors, tenantSelectors } from "store/selectors";
import { getCostShareSubtotal, convertCategoriesToOptions, convertOptionsToTextByValue } from "utils/budgetBuilder";

import styles from "./styles.css";


const getExternalCostShareData = (externalCostShareByPeriod, numberOfPeriods) => {
	let result = {};
	if (externalCostShareByPeriod) {
		for (let periodNumber in externalCostShareByPeriod) {
			const externalCostShareData = externalCostShareByPeriod[periodNumber];
			if (isNonEmptyArray(externalCostShareData)) {
				for (const costShare of externalCostShareData) {
					const key = costShare.value;
					const costShareData = result[key] || getDefaultMemberDataList(numberOfPeriods, costShare.value);
					costShareData[periodNumber - 1] = costShare;
					result[key] = costShareData;
				}
			}
		}
	}

	return result;
};

const getDefaultMemberDataList = (numberOfPeriods, value) => Array.from(Array(numberOfPeriods).keys())
	.map(() => ({ value }));


export const OverviewExternalCostShare = () => {
	const { currencyFormatter } = useTenantFormatter();
	const { isExpandedState: { isExternalCostShareExpanded }, isExpandedDispatch } = useIsExpandedContext();
	const setIsExternalCostShareExpanded = useCallback(
		isExpanded => isExpandedDispatch({ type: "isExternalCostShareExpanded", isExpanded }),
		[ isExpandedDispatch ],
	);

	const categories = useSelector(tenantSelectors.proposalCategories);

	const categoryOptions = useMemo(
		() => convertCategoriesToOptions(categories),
		[ categories ]
	);

	const categoryTextByValue = useMemo(
		() => convertOptionsToTextByValue(categoryOptions),
		[ categoryOptions ]
	);

	useEffect(() => {
		isExpandedDispatch({ type: "isExternalCostShareExpanded", isExpanded: true });
	}, [ isExpandedDispatch ]);

	const periodList = useSelector(budgetBuilderSelectors.periodRows);
	const externalCostShareByPeriod = useSelector(budgetBuilderSelectors.externalCostShareByPeriod);
	const allExternalCostShareItems = useMemo(
		() => {
			let items = [];
			for (const periodNumber in externalCostShareByPeriod) {
				items = [ ...items, ...externalCostShareByPeriod[periodNumber] ];
			}

			return items;
		},
		[ externalCostShareByPeriod ]
	);
	const externalCostShareData = useMemo(
		() => getExternalCostShareData(externalCostShareByPeriod, periodList.length),
		[ externalCostShareByPeriod, periodList.length ]
	);

	const costShareEntries = externalCostShareByPeriod ? Object.entries(externalCostShareByPeriod) : [];

	const toggleExpanded = () => setIsExternalCostShareExpanded(!isExternalCostShareExpanded);

	return (
		<>
			<Table.Row>
				<Table.Cell className={`${styles.cellAlignLeft} ${styles.expandCell}`}>
					<span>{"External Cost Share"}</span>
					<DropdownCaret
						isExpanded={isExternalCostShareExpanded}
						onClick={toggleExpanded}
					/>
				</Table.Cell>
				{isExternalCostShareExpanded && periodList?.map((period, index) => (
					<Table.Cell key={`period-${index}`}>
						<div className={styles.cellAlignRight}>
							<span className={styles.bold}>{"Amount"}</span>
						</div>
					</Table.Cell>
				))}
				{!isExternalCostShareExpanded && costShareEntries.map(([ index, period ]) => (
					<Table.Cell className={styles.cellAlignRight} key={`summary-${index}`}>
						<span className={styles.bold}>{currencyFormatter(getCostShareSubtotal(period))}</span>
					</Table.Cell>
				))}
				<Table.Cell className={styles.cellAlignRight}>
					{!isExternalCostShareExpanded && (
						<span className={styles.bold}>
							{currencyFormatter(getCostShareSubtotal(allExternalCostShareItems))}
						</span>
					)}
				</Table.Cell>
			</Table.Row>
			{isExternalCostShareExpanded && Object.entries(externalCostShareData).map(([ index, items ]) => (
				<Table.Row key={`external-cost-share-item-${index}`}>
					<Table.Cell className={styles.cellAlignLeft}>
						<span>{categoryTextByValue[items[0].value]}</span>
					</Table.Cell>
					{
						items.map((item, p) => (
							<Table.Cell key={`period-${p}`}>
								<div className={styles.cellAlignRight}>
									<span>{currencyFormatter(item.costShareAmount, 0)}</span>
								</div>
							</Table.Cell>
						))
					}
					<Table.Cell className={styles.cellAlignRight}>
						<span className={styles.bold}>
							{currencyFormatter(getCostShareSubtotal(items))}
						</span>
					</Table.Cell>
				</Table.Row>
			))}
			{isExternalCostShareExpanded && (
				<Table.Row>
					<Table.Cell className={styles.cellAlignLeft}>
						<span className={styles.bold}>{"External Cost Share Total"}</span>
					</Table.Cell>
					{
						costShareEntries.map(([ p, period ]) => (
							<Table.Cell className={styles.cellAlignRight} key={`summary-${p}`}>
								<span className={styles.bold}>{currencyFormatter(getCostShareSubtotal(period))}</span>
							</Table.Cell>
						))
					}
					<Table.Cell className={styles.cellAlignRight}>
						<span className={styles.bold}>{currencyFormatter(getCostShareSubtotal(allExternalCostShareItems))}</span>
					</Table.Cell>
				</Table.Row>
			)}
		</>
	);
};
